import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

const Button = forwardRef(function Button(
  {
    children,
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    icon: Icon,
    iconPosition = 'left',
    className = '',
    ...props
  },
  ref
) {
  const baseStyles = `
    inline-flex items-center justify-center gap-2
    font-medium rounded-lg transition-all duration-200
    focus:outline-none focus-ring
    disabled:opacity-50 disabled:cursor-not-allowed
    btn ripple-effect
  `;

  const variants = {
    primary: `
      bg-[#589DD5] text-white
      hover:bg-[#4a8bc4]
      active:bg-[#3d7ab3]
      dark:bg-blue-500 dark:hover:bg-blue-600
    `,
    secondary: `
      bg-gray-100 text-gray-700
      hover:bg-gray-200
      active:bg-gray-300
      dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600
    `,
    outline: `
      border-2 border-[#589DD5] text-[#589DD5]
      hover:bg-[#589DD5]/10
      active:bg-[#589DD5]/20
      dark:border-blue-400 dark:text-blue-400
    `,
    ghost: `
      text-gray-600
      hover:bg-gray-100
      active:bg-gray-200
      dark:text-gray-300 dark:hover:bg-gray-700
    `,
    danger: `
      bg-red-500 text-white
      hover:bg-red-600
      active:bg-red-700
    `,
    success: `
      bg-green-500 text-white
      hover:bg-green-600
      active:bg-green-700
    `,
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
    xl: 'px-6 py-3 text-lg',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6',
  };

  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      disabled={isDisabled}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className={`${iconSizes[size]} animate-spin`} />
          <span>{typeof children === 'string' ? 'Loading...' : children}</span>
        </>
      ) : (
        <>
          {Icon && iconPosition === 'left' && <Icon className={iconSizes[size]} />}
          {children}
          {Icon && iconPosition === 'right' && <Icon className={iconSizes[size]} />}
        </>
      )}
    </button>
  );
});

export default Button;

// Icon button variant
export const IconButton = forwardRef(function IconButton(
  // eslint-disable-next-line no-unused-vars -- Icon is used below
  { icon: Icon, size = 'md', variant = 'ghost', className = '', ...props },
  ref
) {
  const sizes = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-2.5',
    xl: 'p-3',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-7 h-7',
  };

  return (
    <Button
      ref={ref}
      variant={variant}
      className={`${sizes[size]} ${className}`}
      {...props}
    >
      <Icon className={iconSizes[size]} />
    </Button>
  );
});
